<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'
import Header from './component/header.vue'

const store      = useStore()
const permission = computed(() => store.state.getCurrentUserPermission)

const loading     = ref(false)
const saving      = ref(false)
const reviews     = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const cycles      = ref([])
const employees   = ref([])
const cycleFilter  = ref('')
const statusFilter = ref('')
const page         = ref(1)
const showModal    = ref(false)
const formError    = ref('')

const blankForm = () => ({
  cycle_id: '', employee_id: '', reviewer_id: '',
  review_period_start: '', review_period_end: '',
})
const form = ref(blankForm())

const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (cycleFilter.value)  params.cycle_id = cycleFilter.value
    if (statusFilter.value) params.status   = statusFilter.value
    const res = await axios.get('/hr/pms/reviews', { params })
    reviews.value = res.data.data
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadMeta = async () => {
  try {
    const [c, e] = await Promise.all([
      axios.get('/hr/pms/cycles'),
      axios.get('/payroll/employees/active'),
    ])
    cycles.value    = c.data.data?.data ?? c.data.data ?? []
    employees.value = e.data.data ?? []
  } catch (e) { console.error(e) }
}

watch([cycleFilter, statusFilter], () => { page.value = 1; load() })
watch(page, load)
onMounted(() => { load(); loadMeta() })

const openCreate = () => {
  form.value = blankForm(); formError.value = ''; showModal.value = true
}

const initiate = async () => {
  if (!form.value.cycle_id || !form.value.employee_id) {
    formError.value = 'Cycle and employee are required.'; return
  }
  try {
    saving.value = true; formError.value = ''
    await axios.post('/hr/pms/reviews', form.value)
    showModal.value = false; load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to initiate review'
  } finally { saving.value = false }
}

const statusClass = (s) => ({
  draft: 'badge--grey', in_progress: 'badge--blue',
  submitted: 'badge--orange', approved: 'badge--green', rejected: 'badge--red',
}[s] ?? '')

const scoreColor = (s) => {
  if (!s) return ''
  if (s >= 4) return 'score--high'
  if (s >= 3) return 'score--mid'
  return 'score--low'
}
</script>

<template>
  <div>
    <Header />
    <div class="page">
      <div class="toolbar">
        <h2 class="page-title">Performance Reviews</h2>
        <button v-if="permission?.manage_pms_reviews" class="btn-primary" @click="openCreate">+ Initiate Review</button>
      </div>

      <div class="filters">
        <select v-model="cycleFilter" class="filter-select">
          <option value="">All Cycles</option>
          <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.title }}</option>
        </select>
        <select v-model="statusFilter" class="filter-select">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      <div class="table-card">
        <table class="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Reviewer</th>
              <th>Cycle</th>
              <th>Overall Score</th>
              <th>Status</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading"><td colspan="7" class="tbl-empty">Loading…</td></tr>
            <tr v-else-if="!reviews.data?.length"><td colspan="7" class="tbl-empty">No reviews found</td></tr>
            <tr v-for="r in reviews.data" :key="r.id">
              <td><strong>{{ r.employee?.full_name ?? '—' }}</strong></td>
              <td>{{ r.reviewer?.full_name ?? '—' }}</td>
              <td>{{ r.cycle?.title ?? '—' }}</td>
              <td>
                <span v-if="r.overall_score" :class="['score-badge', scoreColor(r.overall_score)]">
                  {{ Number(r.overall_score).toFixed(1) }}
                </span>
                <span v-else>—</span>
              </td>
              <td><span :class="['badge', statusClass(r.status)]">{{ r.status }}</span></td>
              <td>{{ r.submitted_at ?? '—' }}</td>
              <td>
                <router-link :to="$url(`/hr/performance/reviews/${r.id}`)" class="btn-link">
                  {{ r.status === 'draft' || r.status === 'in_progress' ? 'Fill Review' : 'View' }}
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="reviews.last_page > 1" class="pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ reviews.last_page }}</span>
          <button :disabled="page >= reviews.last_page" @click="page++">›</button>
        </div>
      </div>
    </div>

    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>Initiate Performance Review</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Cycle *</label>
              <select v-model="form.cycle_id">
                <option value="">— Select —</option>
                <option v-for="c in cycles" :key="c.id" :value="c.id">{{ c.title }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Employee (Reviewee) *</label>
              <select v-model="form.employee_id">
                <option value="">— Select —</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.full_name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Reviewer</label>
              <select v-model="form.reviewer_id">
                <option value="">— Same as manager —</option>
                <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.full_name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Review Period Start</label>
              <input type="date" v-model="form.review_period_start" />
            </div>
            <div class="form-group">
              <label>Review Period End</label>
              <input type="date" v-model="form.review_period_end" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="initiate">{{ saving ? 'Initiating…' : 'Initiate Review' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page { padding: 0 24px 40px; }
.toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.page-title { font-size: 20px; font-weight: 700; color: #1a2a45; margin: 0; }
.filters { display: flex; gap: 10px; margin-bottom: 16px; }
.filter-select { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; }
.table-card { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.data-table { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.tbl-empty { text-align: center; color: #999; padding: 40px; }
.score-badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.score--high { background: #d1e7dd; color: #0f5132; }
.score--mid  { background: #fff3cd; color: #856404; }
.score--low  { background: #f8d7da; color: #842029; }
.badge { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; }
.badge--grey   { background: #f0f3fa; color: #556; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--blue   { background: #cfe2ff; color: #084298; }
.badge--orange { background: #fff3cd; color: #856404; }
.badge--red    { background: #f8d7da; color: #842029; }
.pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0; text-decoration: none; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 600px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group--full { grid-column: 1 / -1; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
</style>
